// theme.js
// -----------------------------------------------------------------------------
// New Horizon Vineyard (NHV): shared MUI Joy design-system theme.
//
// Anchor brand palette (decided by the client, do NOT change the anchors):
//   - primary  : deep navy  #1E3A5F  (mapped to primary.500)
//   - accent   : orange     #F58025  (mapped to accent.500)
//
// Everything else (tints, shades, neutrals, surfaces, text) is derived from
// these anchors for a crisp, confident light palette in the spirit of pnc.com:
// deep navy as the dominant brand color, orange as the single high-energy
// accent, white surfaces and cool (not warm) light grays.
//
// Page agents: reference TOKENS, not raw hex. Examples:
//   sx={{ bgcolor: 'primary.500', color: 'common.white' }}
//   sx={{ bgcolor: 'background.level1' }}
//   color="primary" variant="solid"   // Button / Chip / etc.
//   textColor="accent.600"            // Typography
// -----------------------------------------------------------------------------

import { extendTheme } from '@mui/joy/styles';

// Heading + body font families. The Google Fonts <link> lives in
// public/index.html; index.css mirrors the body default.
const FONT_HEADING = '"Poppins", "Segoe UI", system-ui, -apple-system, sans-serif';
const FONT_BODY = '"Nunito Sans", "Segoe UI", system-ui, -apple-system, sans-serif';

// Brand anchor hexes (single source of truth for the derived scales below).
const BRAND_PRIMARY = '#1E3A5F'; // deep navy
const BRAND_ACCENT = '#F58025'; // orange

// Deep-navy primary scale, derived around #1E3A5F (lives at 500).
const PRIMARY_SCALE = {
  50: '#EAF0F7',
  100: '#CBD9EA',
  200: '#A6BDD8',
  300: '#7B9CC1',
  400: '#4E77A5',
  500: BRAND_PRIMARY, // #1E3A5F, brand anchor
  600: '#18314F',
  700: '#12273F',
  800: '#0C1D30',
  900: '#071324',
};

// Orange accent scale, derived around #F58025 (lives at 500).
// Contrast note: use dark text (#1A1A1A) on 500 and lighter; white is OK on 600+.
const ACCENT_SCALE = {
  50: '#FFF3E8',
  100: '#FFE0C7',
  200: '#FFC79C',
  300: '#FFAA6B',
  400: '#FA9440',
  500: BRAND_ACCENT, // #F58025, brand anchor
  600: '#D96A14',
  700: '#B5560E',
  800: '#8C420B',
  900: '#632E08',
};

// Cool neutral scale (blue-leaning grays so surfaces read crisp, not warm).
const NEUTRAL_SCALE = {
  50: '#F7F9FB',
  100: '#EEF2F6',
  200: '#DDE3EA',
  300: '#C4CDD8',
  400: '#97A3B3',
  500: '#6B7A8C',
  600: '#4F5D6E',
  700: '#364454',
  800: '#212C3A',
  900: '#121A24',
};

export const theme = extendTheme({
  // Friendly, rounded radii.
  radius: {
    xs: '4px',
    sm: '8px',
    md: '12px',
    lg: '16px',
    xl: '24px',
  },

  // Soft, layered shadows (cool-tinted, low contrast).
  shadow: {
    xs: '0 1px 2px rgba(18, 26, 36, 0.06)',
    sm: '0 2px 8px rgba(18, 26, 36, 0.08)',
    md: '0 6px 20px rgba(18, 26, 36, 0.10)',
    lg: '0 14px 40px rgba(18, 26, 36, 0.12)',
    xl: '0 24px 64px rgba(18, 26, 36, 0.16)',
  },

  fontFamily: {
    display: FONT_HEADING,
    body: FONT_BODY,
  },

  colorSchemes: {
    light: {
      palette: {
        primary: {
          ...PRIMARY_SCALE,
          solidBg: 'var(--joy-palette-primary-500)',
          solidHoverBg: 'var(--joy-palette-primary-600)',
          solidActiveBg: 'var(--joy-palette-primary-700)',
          plainColor: 'var(--joy-palette-primary-600)',
          outlinedColor: 'var(--joy-palette-primary-600)',
        },
        // Custom brand accent channel, referenced as color="accent".
        accent: {
          ...ACCENT_SCALE,
          solidBg: 'var(--joy-palette-accent-500)',
          solidColor: '#1A1A1A',
          solidHoverBg: 'var(--joy-palette-accent-600)',
          solidActiveBg: 'var(--joy-palette-accent-700)',
          plainColor: 'var(--joy-palette-accent-700)',
          outlinedColor: 'var(--joy-palette-accent-700)',
        },
        neutral: {
          ...NEUTRAL_SCALE,
        },
        background: {
          body: '#ffffff',
          surface: '#ffffff',
          level1: NEUTRAL_SCALE[50], // cool off-white section tint
          level2: NEUTRAL_SCALE[100],
          popup: '#ffffff',
        },
        text: {
          primary: NEUTRAL_SCALE[900], // near-black
          secondary: NEUTRAL_SCALE[700],
          tertiary: NEUTRAL_SCALE[600],
        },
        common: {
          white: '#ffffff',
          black: '#121A24',
        },
      },
    },
  },

  // Welcoming, accessible typographic scale.
  typography: {
    // Oversized friendly hero display.
    display: {
      fontFamily: FONT_HEADING,
      fontWeight: 800,
      fontSize: 'clamp(2.6rem, 5.5vw, 4.25rem)',
      lineHeight: 1.05,
      letterSpacing: '-0.02em',
      color: 'var(--joy-palette-text-primary)',
    },
    h1: {
      fontFamily: FONT_HEADING,
      fontWeight: 700,
      fontSize: 'clamp(2rem, 3.6vw, 2.85rem)',
      lineHeight: 1.12,
      letterSpacing: '-0.015em',
      color: 'var(--joy-palette-text-primary)',
    },
    h2: {
      fontFamily: FONT_HEADING,
      fontWeight: 700,
      fontSize: 'clamp(1.65rem, 2.8vw, 2.2rem)',
      lineHeight: 1.18,
      letterSpacing: '-0.01em',
      color: 'var(--joy-palette-text-primary)',
    },
    h3: {
      fontFamily: FONT_HEADING,
      fontWeight: 600,
      fontSize: 'clamp(1.35rem, 2.1vw, 1.6rem)',
      lineHeight: 1.25,
      color: 'var(--joy-palette-text-primary)',
    },
    h4: {
      fontFamily: FONT_HEADING,
      fontWeight: 600,
      fontSize: '1.2rem',
      lineHeight: 1.3,
      color: 'var(--joy-palette-text-primary)',
    },
    // Section eyebrow / kicker.
    title: {
      fontFamily: FONT_HEADING,
      fontWeight: 700,
      fontSize: '0.8rem',
      lineHeight: 1.4,
      letterSpacing: '0.12em',
      textTransform: 'uppercase',
      color: 'var(--joy-palette-primary-600)',
    },
    'title-lg': {
      fontFamily: FONT_HEADING,
      fontWeight: 600,
      fontSize: '1.1rem',
      lineHeight: 1.4,
      color: 'var(--joy-palette-text-primary)',
    },
    'title-md': {
      fontFamily: FONT_HEADING,
      fontWeight: 600,
      fontSize: '1rem',
      lineHeight: 1.4,
      color: 'var(--joy-palette-text-primary)',
    },
    'title-sm': {
      fontFamily: FONT_HEADING,
      fontWeight: 600,
      fontSize: '0.875rem',
      lineHeight: 1.4,
      color: 'var(--joy-palette-text-primary)',
    },
    'body-lg': {
      fontFamily: FONT_BODY,
      fontWeight: 400,
      fontSize: '1.15rem',
      lineHeight: 1.65,
      color: 'var(--joy-palette-text-secondary)',
    },
    'body-md': {
      fontFamily: FONT_BODY,
      fontWeight: 400,
      fontSize: '1rem',
      lineHeight: 1.6,
      color: 'var(--joy-palette-text-secondary)',
    },
    'body-sm': {
      fontFamily: FONT_BODY,
      fontWeight: 400,
      fontSize: '0.875rem',
      lineHeight: 1.55,
      color: 'var(--joy-palette-text-tertiary)',
    },
    'body-xs': {
      fontFamily: FONT_BODY,
      fontWeight: 600,
      fontSize: '0.75rem',
      lineHeight: 1.5,
      letterSpacing: '0.04em',
      color: 'var(--joy-palette-text-tertiary)',
    },
  },

  // Friendly component defaults.
  components: {
    JoyButton: {
      styleOverrides: {
        root: ({ theme: t }) => ({
          fontFamily: FONT_HEADING,
          fontWeight: 600,
          borderRadius: t.radius.lg,
          textTransform: 'none',
          letterSpacing: '0.01em',
          transition: 'transform 120ms ease, box-shadow 120ms ease, background-color 120ms ease',
          '&:hover': {
            transform: 'translateY(-1px)',
          },
          '&:active': {
            transform: 'translateY(0)',
          },
        }),
      },
    },
    JoyCard: {
      styleOverrides: {
        root: ({ theme: t }) => ({
          borderRadius: t.radius.lg,
          boxShadow: t.shadow.sm,
          borderColor: t.palette.neutral[200],
        }),
      },
    },
    JoyInput: {
      styleOverrides: {
        root: ({ theme: t }) => ({
          borderRadius: t.radius.md,
          minHeight: '44px',
        }),
      },
    },
    JoyTextarea: {
      styleOverrides: {
        root: ({ theme: t }) => ({
          borderRadius: t.radius.md,
        }),
      },
    },
    JoyChip: {
      styleOverrides: {
        root: ({ theme: t }) => ({
          borderRadius: t.radius.xl,
          fontFamily: FONT_HEADING,
          fontWeight: 600,
        }),
      },
    },
  },
});

export default theme;
